import React from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import LogoutIcon from "@mui/icons-material/Logout";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import {
  logoutUser,
} from "../../../services/account.service";
import {
  getBusinessProfile,
  updateBusinessProfile,
  businessUploadLogo,
} from "../../../services/business.service";
import Loader from "../../../common/loader";
import {slugifyName} from "../../../helper/common.helper";
import { useUser } from "../../../context/UserContext";
import logo from "../../../assets/Logo.jpg";

const defaultSignatureIcon =
  "https://cdn.icon-icons.com/icons2/1765/PNG/512/hand-outline-gesture-7_113971.png";

const defaultLogoIcon = logo;

interface Props {
 businessId: string;
}

const BusinessAccount: React.FC<Props> = ({businessId}) => {
  const navigate = useNavigate();
  const { setBusiness, setUser } = useUser();

const [formData, setFormData] = React.useState({
    name: "",
    ein: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zipcode: "",
    website: "",
    synonyms: [""],
    businessSlug: ""
  });

  const [signatureImageUrl, setSignatureImageUrl] =
    React.useState(defaultSignatureIcon);
  const [logoImageUrl, setLogoImageUrl] = React.useState(defaultLogoIcon);
  const [loading, setLoading] = React.useState(true);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertSeverity, setAlertSeverity] = React.useState<
    "success" | "error" | "info"
  >("success");

  React.useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (businessId) {
          await loadBusinessProfile(businessId);
        }
      } catch (err) {
        setAlertSeverity("error");
        setAlertMessage("Failed to load user data.");
        setAlertOpen(true);
      } finally {
        setLoading(false);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBusinessProfile = async (id: string) => {
    try {
      const business = await getBusinessProfile(id);
      if (business) {
      setFormData({
        name: business.name || "",
        ein: business.ein || "",
        email: business.email || "",
        phone: business.phone || "",
        street: business.street || "",
        city: business.city || "",
        state: business.state || "",
        zipcode: business.zipcode || "",
        website: business.website || "",
        synonyms: business.synonyms?.length ? business.synonyms : [""],
        businessSlug: business.businessSlug
      });
        setSignatureImageUrl(
          business.signature
            ? `${business.signature}`
            : defaultSignatureIcon
        );
        setLogoImageUrl(
          business.logo
            ? `${business.logo}`
            : defaultLogoIcon
        );
        // Store business data in global context
        setBusiness(business);
        setUser(business); // Since business and user are the same for business accounts
      }
    } catch (err) {
      setAlertSeverity("error");
      setAlertMessage("Failed to load business profile.");
      setAlertOpen(true);
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    ein: Yup.string()
      .matches(/^\d{9}$/, "EIN must be a 9-digit number")
      .required("EIN is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string(), // optional
    street: Yup.string(), // optional
    city: Yup.string(), // optional
    state: Yup.string(), // optional
    zipcode: Yup.string(), // optional
    website: Yup.string().url("Invalid URL"), // optional
    synonyms: Yup.array()
      .of(Yup.string().notRequired())
      .max(5, "Maximum 5 synonyms allowed"),
  });

  const handleSubmit = async (values: typeof formData) => {
    setLoading(true);
    try {
      const updatedFields: Partial<typeof formData> = {};

      if (formData.name !== values.name){
        updatedFields.name = values.name;
        updatedFields.businessSlug = slugifyName(values.name);
      }
      if (formData.ein !== values.ein) updatedFields.ein = values.ein;
      if (formData.email !== values.email) updatedFields.email = values.email;
      if (formData.phone !== values.phone) updatedFields.phone = values.phone;
      if (formData.street !== values.street) updatedFields.street = values.street;
      if (formData.city !== values.city) updatedFields.city = values.city;
      if (formData.state !== values.state) updatedFields.state = values.state;
      if (formData.zipcode !== values.zipcode) updatedFields.zipcode = values.zipcode;
      if (formData.website !== values.website)
        updatedFields.website = values.website;
      if (
        JSON.stringify(formData.synonyms) !== JSON.stringify(values.synonyms)
      ) {
        updatedFields.synonyms = values.synonyms;
      }

      if (Object.keys(updatedFields).length === 0) {
        setAlertSeverity("info");
        setAlertMessage("No changes made.");
      } else {
        await updateBusinessProfile(businessId, updatedFields);
        setAlertSeverity("success");
        setAlertMessage("Business profile updated successfully!");
        await loadBusinessProfile(businessId); // Refresh data
      }
    } catch (error: any) {
      setAlertSeverity("error");
      setAlertMessage(error?.message || "Failed to update business profile.");
    } finally {
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
      try {
        const uploadData = {
          id: businessId,
          logo: file,
        };
        setLoading(true);
        await businessUploadLogo(uploadData);
        setAlertSeverity("success");
        setAlertMessage("Business logo uploaded successfully!");
        setAlertOpen(true);
        await loadBusinessProfile(businessId); // Refresh to get new logo
      } catch (error) {
        setAlertSeverity("error");
        setAlertMessage("Failed to upload logo.");
        setAlertOpen(true);
      } finally {
        setLoading(false);
      }
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate("/login");
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div
        className="max-w-full p-2 sm:p-4 rounded-xl border border-gray-200"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
          <div className="text-center sm:text-left">
            <Typography variant="h5" className="font-bold text-gray-800">
              Business Profile
            </Typography>
            <Typography variant="subtitle2" className="text-gray-600">
              Keep your details up to date
            </Typography>
          </div>
          <div className="flex items-center gap-8">
            {/* Logo Section */}
            <div className="flex flex-col items-center gap-2">
              <div className="relative">
                <div className="p-1 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                  <img
                    src={logoImageUrl}
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.onerror = null;
                      target.src = defaultLogoIcon;
                    }}
                    alt="Business Logo"
                    className="rounded-lg border-2 border-white/50 bg-white shadow-sm"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "contain"
                    }}
                  />
                </div>
                <label 
                  htmlFor="logo-upload" 
                  className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-full flex items-center gap-1 cursor-pointer shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap"
                  title="Upload new business logo"
                >
                  <EditIcon style={{ fontSize: 14 }} />
                  Change
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-1">Logo</span>
            </div>
            {/* Divider */}
            <div className="h-16 w-px bg-gradient-to-b from-gray-300 via-gray-200 to-gray-300"></div>
            {/* Signature Section */}
            <div className="flex flex-col items-center gap-2">
              <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                <img
                  src={signatureImageUrl}
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.onerror = null;
                    target.src = defaultSignatureIcon;
                  }}
                  alt="Signature"
                  className="rounded-md"
                  style={{
                    width: "auto",
                    height: "50px",
                    maxWidth: "150px",
                    objectFit: "contain"
                  }}
                />
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Signature</span>
            </div>
          </div>
        </div>

        <Formik
          initialValues={formData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched }) => (
            <Form className="space-y-8">
              <Box className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  name="name"
                  as={TextField}
                  label="Business Name"
                  fullWidth
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="ein"
                  as={TextField}
                  label="EIN"
                  fullWidth
                  error={Boolean(touched.ein && errors.ein)}
                  helperText={touched.ein && errors.ein}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="email"
                  as={TextField}
                  label="Email"
                  fullWidth
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="phone"
                  as={TextField}
                  label="Phone"
                  fullWidth
                  error={Boolean(touched.phone && errors.phone)}
                  helperText={touched.phone && errors.phone}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="street"
                  as={TextField}
                  label="Street"
                  fullWidth
                  error={Boolean(touched.street && errors.street)}
                  helperText={touched.street && errors.street}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="city"
                  as={TextField}
                  label="City"
                  fullWidth
                  error={Boolean(touched.city && errors.city)}
                  helperText={touched.city && errors.city}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="state"
                  as={TextField}
                  label="State"
                  fullWidth
                  error={Boolean(touched.state && errors.state)}
                  helperText={touched.state && errors.state}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="zipcode"
                  as={TextField}
                  label="Zipcode"
                  fullWidth
                  error={Boolean(touched.zipcode && errors.zipcode)}
                  helperText={touched.zipcode && errors.zipcode}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
                <Field
                  name="website"
                  as={TextField}
                  label="Website"
                  fullWidth
                  error={Boolean(touched.website && errors.website)}
                  helperText={touched.website && errors.website}
                  size="medium" InputProps={{style: {borderRadius: 10}}}
                />
              </Box>

              {/* Synonyms */}
              <Box className="bg-gray-50 p-5 border border-gray-200 rounded-md">
                <Typography
                  variant="h6"
                  className="mb-4 text-gray-800 font-semibold"
                >
                  Doing Business  As(DBA)
                </Typography>
                <FieldArray name="synonyms">
                  {({ push, remove }) => (
                    <div className="space-y-4">
                      {values.synonyms.map((_, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Field
                            name={`synonyms.${index}`}
                            as={TextField}
                            placeholder={`DBA ${index + 1}`}
                            fullWidth
                            error={Boolean(
                              (touched.synonyms as any[] | undefined)?.[
                                index
                              ] &&
                                (errors.synonyms as any[] | undefined)?.[index]
                            )}
                            helperText={
                              (touched.synonyms as any[] | undefined)?.[
                                index
                              ] &&
                              (errors.synonyms as any[] | undefined)?.[index]
                                ? (errors.synonyms as any[])[index]
                                : ""
                            }
                            size="medium" InputProps={{style: {borderRadius: 10}}}
                          />
                          <IconButton
                            onClick={() => remove(index)}
                            color="error"
                            disabled={values.synonyms.length === 1}
                          >
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        </div>
                      ))}
                      {values.synonyms.length < 5 && (
                        <Button
                          type="button"
                          onClick={() => push("")}
                          startIcon={<AddIcon />}
                          variant="text"
                          size="small"
                        >
                          Add DBA
                        </Button>
                      )}
                    </div>
                  )}
                </FieldArray>
              </Box>

              {/* Submit & Logout */}
              <Box className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' },
                    order: { xs: 2, sm: 1 }
                  }}
                >
                  Logout
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' },
                    order: { xs: 1, sm: 2 }
                  }}
                  disabled={loading}
                >
                  Save Business
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BusinessAccount;
